import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismButton,
  PrismDrawer,
  PrismIcon,
  PrismLoadingBar,
  PrismTabsContainer,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, eq, not, or } from 'ember-truth-helpers';
import GeneralSmartInsightsCalendarCalendarSmartInsights from 'lumen/components/general/smart-insights/calendar/calendar-smart-insights';
import GeneralSmartInsightsDetailViewListArchivedInsights from 'lumen/components/general/smart-insights/detail-view/list-archived-insights';
import GeneralSmartInsightsDetailViewSmartInsightDetailView from 'lumen/components/general/smart-insights/detail-view/smart-insight-detail-view';
import GeneralSmartInsightsDropdownPriorityFilter from 'lumen/components/general/smart-insights/dropdown-priority-filter';
import GeneralSmartInsightsListDropdownSort from 'lumen/components/general/smart-insights/list/dropdown-sort';
import GeneralSmartInsightsListListSmartInsights from 'lumen/components/general/smart-insights/list/list-smart-insights';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';
import type { SmartInsightDay } from 'lumen/models';
import type { SmartInsightPriority } from 'lumen/models/smart-insights/smart-insight-day';
import type { DrawerID } from 'lumen/services/drawer';
import {
  DRAWER_IDS,
  HEADER_HEIGHT,
  SECOND_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import {
  isSmartInsightsTab,
  isSmartInsightsView,
  SMART_INSIGHTS_ARCHIVE_REASONS,
  SMART_INSIGHTS_LIST_SORT_OPTIONS,
  SMART_INSIGHTS_TABS,
  SMART_INSIGHTS_VIEWS,
  type SmartInsightsArchiveReason,
  type SmartInsightsKpiComparisonPeriod,
  type SmartInsightsListSortOption,
  type SmartInsightsTab,
  type SmartInsightsView,
} from 'lumen/utils/constants/smart-insights';

import styles from './smart-insights-drawer.module.css';

interface GeneralSmartInsightsSmartInsightsDrawerSignature {
  Args: {
    selectedPriorities: SmartInsightPriority[];
    selectedSmartInsight: SmartInsightDay | null;
    setSelectedPriorities: (priorities: SmartInsightPriority[]) => void;
    setSmartInsightsDay: (day: string | null) => void;
    setSmartInsightsKpiComparisonPeriod: (
      period: SmartInsightsKpiComparisonPeriod,
    ) => void;
    setSmartInsightsListSortOption: (
      sortOption: SmartInsightsListSortOption,
    ) => void;
    setSmartInsightsTab: (tab: SmartInsightsTab) => void;
    setSmartInsightsView: (view: SmartInsightsView) => void;
    smartInsightsDay: string | null | undefined;
    smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod;
    smartInsightsListSortOption: SmartInsightsListSortOption;
    smartInsightsTab: SmartInsightsTab;
    smartInsightsView: SmartInsightsView;
    toggleSmartInsightsDrawer: () => void;
  };
  Element: HTMLButtonElement;
}

export default class GeneralSmartInsightsSmartInsightsDrawer extends Component<GeneralSmartInsightsSmartInsightsDrawerSignature> {
  @service declare smartInsights: Services['smart-insights'];

  @service declare usageAnalytics: Services['usage-analytics'];

  SMART_INSIGHTS_DRAWER_ID: DrawerID = DRAWER_IDS.SMART_INSIGHTS_DRAWER;
  SMART_INSIGHTS_TABS = SMART_INSIGHTS_TABS;

  get currentInsightIndex(): number {
    const { selectedSmartInsight } = this.args;

    if (!selectedSmartInsight) {
      return -1;
    }

    return this.sortedFilteredInsights.findIndex(
      (insight) => insight.stayDate === selectedSmartInsight.stayDate,
    );
  }

  get drawerContentHeight(): SafeString {
    return htmlSafe(`height: calc(100vh - ${this.drawerPositionTop}px);`);
  }

  get drawerPositionTop(): number {
    return HEADER_HEIGHT - SECOND_HEADER_HEIGHT;
  }

  get filteredInsights(): SmartInsightDay[] {
    return this.insights.filter(
      (insight) =>
        this.args.selectedPriorities.includes(insight.stayDatePriority) &&
        insight.insightEntries.length > 0,
    );
  }

  get hasNextInsight(): boolean {
    return !!this.nextInsight;
  }

  get hasPreviousInsight(): boolean {
    return !!this.previousInsight;
  }

  get insights(): SmartInsightDay[] {
    if (
      !this.smartInsights.getSmartInsightsTask.last ||
      this.smartInsights.getSmartInsightsTask.last.isError
    ) {
      return [];
    }

    return this.smartInsights.getSmartInsightsTask.last.value ?? [];
  }

  get insightsSortedByDate(): SmartInsightDay[] {
    return this.insights.slice().sort((a, b) => {
      if (a.stayDate === b.stayDate) {
        return b.stayDateRelevance - a.stayDateRelevance;
      }

      return new Date(a.stayDate).getTime() - new Date(b.stayDate).getTime();
    });
  }

  get insightsSortedByPriority(): SmartInsightDay[] {
    return this.insights.slice().sort((a, b) => {
      if (a.stayDateRelevance === b.stayDateRelevance) {
        return new Date(a.stayDate).getTime() - new Date(b.stayDate).getTime();
      }

      return b.stayDateRelevance - a.stayDateRelevance;
    });
  }

  get nextInsight(): SmartInsightDay | undefined {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return undefined;
    }

    if (this.currentInsightIndex >= 0) {
      return this.sortedFilteredInsights[this.currentInsightIndex + 1];
    }

    // Selected day has no entries or no record; find the first day after it with entries
    return this.sortedFilteredInsights.find(
      (insight) => insight.stayDate > smartInsightsDay,
    );
  }

  get nextInsightInfo(): { date: string; onReview: () => void } | undefined {
    const stayDate = this.nextInsight?.stayDate;

    if (!stayDate) {
      return undefined;
    }

    return {
      date: dayjs(stayDate).format('ddd, MMM D'),
      onReview: this.onClickNext.bind(this),
    };
  }

  get previousInsight(): SmartInsightDay | undefined {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return undefined;
    }

    if (this.currentInsightIndex === 0) {
      return undefined;
    }

    if (this.currentInsightIndex > 0) {
      return this.sortedFilteredInsights[this.currentInsightIndex - 1];
    }

    // Selected day has no entries or no record; find the last day before it with entries
    const allPrevious = this.sortedFilteredInsights.filter(
      (insight) => insight.stayDate < smartInsightsDay,
    );
    return allPrevious.at(-1);
  }

  /** Show the sort dropdown only in list view when no specific insight or day is selected. */
  get showDropdownSort(): boolean {
    const { smartInsightsView, selectedSmartInsight, smartInsightsDay } =
      this.args;

    if (selectedSmartInsight || this.showEmptyState || smartInsightsDay) {
      return false;
    }

    return smartInsightsView === SMART_INSIGHTS_VIEWS.LIST;
  }

  get showEmptyState(): boolean {
    // Show empty state when selectedSmartInsight is explicitly undefined
    // (meaning a smartInsightsDay was requested but no data exists for it)
    return !!(
      this.args.smartInsightsDay &&
      !this.args.selectedSmartInsight &&
      !this.smartInsights.getSmartInsightsTask.isRunning
    );
  }

  /** Show the priority filter only in calendar view when no specific insight or day is selected. */
  get showPriorityFilter(): boolean {
    const { smartInsightsView, selectedSmartInsight, smartInsightsDay } =
      this.args;

    if (selectedSmartInsight || this.showEmptyState || smartInsightsDay) {
      return false;
    }

    return smartInsightsView === SMART_INSIGHTS_VIEWS.CALENDAR;
  }

  get sortedFilteredInsights(): SmartInsightDay[] {
    if (this.args.smartInsightsView === SMART_INSIGHTS_VIEWS.CALENDAR) {
      return this.sortedInsights.filter(
        (insight) =>
          this.args.selectedPriorities.includes(insight.stayDatePriority) &&
          insight.insightEntries.length > 0,
      );
    }

    return this.sortedInsights.filter(
      (insight) => insight.insightEntries.length > 0,
    );
  }

  get sortedInsights(): SmartInsightDay[] {
    const { smartInsightsView, smartInsightsListSortOption } = this.args;

    // Calendar view always sorts by date
    if (smartInsightsView === SMART_INSIGHTS_VIEWS.CALENDAR) {
      return this.insightsSortedByDate;
    }

    // List view respects the sort option
    if (smartInsightsView === SMART_INSIGHTS_VIEWS.LIST) {
      switch (smartInsightsListSortOption) {
        case SMART_INSIGHTS_LIST_SORT_OPTIONS.PRIORITY:
          return this.insightsSortedByPriority;
        case SMART_INSIGHTS_LIST_SORT_OPTIONS.DATE:
          return this.insightsSortedByDate;
        default:
          return this.insightsSortedByDate;
      }
    }

    // Default to date sorting
    return this.insightsSortedByDate;
  }

  get stayDateFormatted(): string {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return '';
    }

    return dayjs(smartInsightsDay).format('ddd, MMM D');
  }

  @action collapseDrawer(): void {
    this.args.toggleSmartInsightsDrawer();
  }

  @action async onArchiveInsight(
    reason: SmartInsightsArchiveReason,
  ): Promise<void> {
    const { smartInsightsDay } = this.args;

    if (!smartInsightsDay) {
      return;
    }

    try {
      await this.smartInsights.archiveInsightTask.perform(
        smartInsightsDay,
        reason,
        undefined,
        {
          onNavigate: (stayDate: string): void => {
            this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
            this.args.setSmartInsightsDay(stayDate);
          },
          product: 'smart-insights',
        },
      );

      this.usageAnalytics.track('Smart Insights Insight Archived', {
        eventTrigger: 'smart-insights-archive-dropdown',
        product: 'smart-insights',
        actioned: reason === SMART_INSIGHTS_ARCHIVE_REASONS.ACTION_TAKEN,
        ...this.smartInsights.getSmartInsightUsageAnalyticsContext(
          this.args.selectedSmartInsight,
          smartInsightsDay,
        ),
      });

      // After successful archive, go back to calendar/list view
      this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
      this.args.setSmartInsightsDay(null);
    } catch {
      // Error is already handled in the service
    }
  }

  @action onClickBack(): void {
    this.args.setSmartInsightsTab(SMART_INSIGHTS_TABS.ACTIVE);
    this.args.setSmartInsightsDay(null);
  }

  @action onClickNext(): void {
    const next = this.nextInsight;

    if (next) {
      this.args.setSmartInsightsDay(next.stayDate);
      this.smartInsights.fetchArchived(next.stayDate);
    }
  }

  @action onClickPrevious(): void {
    const previous = this.previousInsight;

    if (previous) {
      this.args.setSmartInsightsDay(previous.stayDate);
      this.smartInsights.fetchArchived(previous.stayDate);
    }
  }

  @action onSelectTab(tabId: string): void {
    if (!isSmartInsightsTab(tabId)) {
      return;
    }

    this.args.setSmartInsightsTab(tabId);

    this.usageAnalytics.track('Smart Insights Tab Selected', {
      eventTrigger: 'smart-insights-tabs',
      product: 'smart-insights',
      tab: tabId,
    });
  }

  @action onSetActiveButtonId(buttonId: string): void {
    if (!isSmartInsightsView(buttonId)) {
      return;
    }

    if (this.args.smartInsightsView === buttonId) {
      return;
    }

    this.args.setSmartInsightsView(buttonId);

    this.usageAnalytics.track('Smart Insights Layout Toggled', {
      eventTrigger: 'smart-insights-view-switcher',
      product: 'smart-insights',
      view: buttonId,
    });
  }

  @action selectDate(day: string | null): void {
    this.args.setSmartInsightsDay(day);
    if (day) {
      this.smartInsights.fetchArchived(day);
    }
  }

  @action selectSmartInsightsDay(smartInsightsDay: SmartInsightDay): void {
    this.args.setSmartInsightsDay(smartInsightsDay.stayDate);
    this.smartInsights.fetchArchived(smartInsightsDay.stayDate);
  }

  <template>
    <PrismDrawer
      @contentPadding="none"
      @isOpen={{isDrawerExpanded this.SMART_INSIGHTS_DRAWER_ID}}
      @positionTop={{this.drawerPositionTop}}
      class={{styles.smart-insights-drawer}}
      data-test-id="smart-insights-drawer"
    >
      <:content>
        <div
          class="smart-insights-drawer__content relative flex flex-col"
          style={{this.drawerContentHeight}}
        >
          {{#if this.smartInsights.getSmartInsightsTask.isRunning}}
            <PrismLoadingBar data-test-id="prism-loading-bar" />
          {{/if}}

          <div class={{styles.smart-insights-drawer__header}}>
            <div class="flex w-full flex-row justify-between">
              <div class="grid grid-cols-[auto_1fr] gap-x-200">
                {{#if @smartInsightsDay}}
                  {{! Not using a button to avoid the Smart Insights title from jumping when switching between the calendar and detail view }}
                  <PrismIcon
                    @name="arrow-left"
                    class="cursor-pointer self-center"
                    data-test-id="back-button"
                    role="button"
                    {{on "click" this.onClickBack}}
                  />
                {{else}}
                  <PrismIcon
                    @color="on-intelligence"
                    @name="smart-insights"
                    class="self-center"
                  />
                {{/if}}
                <h3>{{t "smart_insights.title"}}</h3>
                <p class="text-200--regular text-neutral-subdued col-start-2">
                  {{t "smart_insights.powered_by"}}
                </p>
              </div>

              <div class="action">
                <PrismButton
                  @buttonType="ghost"
                  @icon="cross"
                  @isSmall={{true}}
                  @onClick={{this.collapseDrawer}}
                  data-test-id="drawer-close-button"
                />
              </div>
            </div>

            <div
              class="flex min-h-[36px] w-full flex-row items-center
                {{if
                  (or this.showDropdownSort this.showPriorityFilter)
                  'justify-between'
                  (if @smartInsightsDay 'justify-start' 'justify-between')
                }}"
            >
              {{#if this.showDropdownSort}}
                <GeneralSmartInsightsListDropdownSort
                  @setSmartInsightsListSortOption={{@setSmartInsightsListSortOption}}
                  @smartInsightsListSortOption={{@smartInsightsListSortOption}}
                />
              {{else if this.showPriorityFilter}}
                <div
                  class="flex items-center gap-100"
                  data-test-id="dropdown-priority-filter"
                >
                  <span class="text-200--regular">{{t
                      "benchmark_insight.los_chart.show"
                    }}</span>

                  <GeneralSmartInsightsDropdownPriorityFilter
                    @isSmall={{true}}
                    @selectedPriorities={{@selectedPriorities}}
                    @setSelectedPriorities={{@setSelectedPriorities}}
                  />
                </div>
              {{else if @smartInsightsDay}}
                <div class="flex items-center gap-200">
                  <div class="flex gap-100">
                    <PrismButton
                      @buttonType="secondary"
                      @icon="chevron-left"
                      @isDisabled={{not this.hasPreviousInsight}}
                      @isSmall={{true}}
                      @onClick={{this.onClickPrevious}}
                      data-test-id="navigation-previous-button"
                    />
                    <PrismButton
                      @buttonType="secondary"
                      @icon="chevron-right"
                      @isDisabled={{not this.hasNextInsight}}
                      @isSmall={{true}}
                      @onClick={{this.onClickNext}}
                      data-test-id="navigation-next-button"
                    />
                  </div>
                  <div
                    class="text-300--medium text-neutral-subdued"
                    data-test-id="detail-view-date"
                  >
                    {{this.stayDateFormatted}}
                  </div>
                </div>
              {{/if}}

              {{#unless @smartInsightsDay}}
                <div data-test-id="view-switcher">
                  <PrismViewSwitcher
                    @activeButtonId={{@smartInsightsView}}
                    @onSetActiveButtonId={{this.onSetActiveButtonId}}
                    as |ViewSwitcher|
                  >
                    <ViewSwitcher.button
                      @icon="view-calendar"
                      @id={{SMART_INSIGHTS_VIEWS.CALENDAR}}
                      @label=""
                      data-test-id="view-switcher-calendar"
                    />

                    <ViewSwitcher.button
                      @icon="view-list"
                      @id={{SMART_INSIGHTS_VIEWS.LIST}}
                      @label=""
                      data-test-id="view-switcher-list"
                    />
                  </PrismViewSwitcher>
                </div>
              {{/unless}}
            </div>
          </div>

          <div class={{styles.body}}>
            {{#if @smartInsightsDay}}
              {{! template-lint-disable no-duplicate-id }}
              <PrismTabsContainer
                @activeTabId={{@smartInsightsTab}}
                @onSelectTab={{this.onSelectTab}}
                @visibleTabIds={{array
                  this.SMART_INSIGHTS_TABS.ACTIVE
                  this.SMART_INSIGHTS_TABS.ARCHIVED
                }}
                as |TabsContainer|
              >
                <TabsContainer.header>
                  <:default as |Header|>
                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}
                      data-test-tab="active"
                    >
                      {{t "smart_insights.tabs.active"}}
                    </Header.tab>

                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}
                      data-test-tab="archived"
                    >
                      <span class="flex items-center gap-100">
                        {{t "smart_insights.tabs.archived"}}
                        {{#if
                          (and
                            this.smartInsights.archivedInsightDays.length
                            (not
                              this.smartInsights.getArchivedInsightsTask.isRunning
                            )
                          )
                        }}
                          <PrismBadge
                            @badgeType="neutral"
                            @isSmallBadge={{true}}
                            @label={{this.smartInsights.archivedInsightDays.length}}
                          />
                        {{/if}}
                      </span>
                    </Header.tab>
                  </:default>
                </TabsContainer.header>

                <TabsContainer.content as |Content|>
                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}>
                    {{#if this.smartInsights.getArchivedInsightsTask.isRunning}}
                      <PrismLoadingBar
                        data-test-id="archived-insights-loading-active-tab"
                      />
                    {{/if}}

                    {{#if this.showEmptyState}}
                      <div
                        class="rounded-200 bg-elevation-surface m-400 p-500 shadow-neutral-100"
                        data-test-id="empty-state"
                      >
                        <p class="text-300--regular">
                          {{t
                            "smart_insights.empty_state.description"
                            classNameBold="font-bold"
                            htmlSafe=true
                          }}
                        </p>
                        {{#if this.nextInsightInfo}}
                          <div class="divider-line mt-500 mb-300" />
                          <div
                            class="flex items-center justify-between gap-200"
                          >
                            <div class="flex items-center gap-200">
                              <PrismIcon
                                @color="on-intelligence"
                                @name="smart-insights"
                              />
                              <span class="text-300--regular">
                                {{t
                                  "smart_insights.empty_state.next_insight"
                                  classNameBold="font-bold"
                                  date=this.nextInsightInfo.date
                                  htmlSafe=true
                                }}
                              </span>
                            </div>
                            <PrismButton
                              @buttonType="primary"
                              @isSmall={{true}}
                              @label={{t "smart_insights.empty_state.review"}}
                              @onClick={{this.nextInsightInfo.onReview}}
                              data-test-id="review-next-insight-button"
                            />
                          </div>
                        {{/if}}
                      </div>
                    {{else if @selectedSmartInsight}}
                      <GeneralSmartInsightsDetailViewSmartInsightDetailView
                        @isArchiving={{this.smartInsights.archiveInsightTask.isRunning}}
                        @nextInsight={{this.nextInsightInfo}}
                        @onArchive={{this.onArchiveInsight}}
                        @setSmartInsightsKpiComparisonPeriod={{@setSmartInsightsKpiComparisonPeriod}}
                        @smartInsight={{@selectedSmartInsight}}
                        @smartInsightsKpiComparisonPeriod={{@smartInsightsKpiComparisonPeriod}}
                      />
                    {{/if}}
                  </Content.tab>

                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}>
                    <GeneralSmartInsightsDetailViewListArchivedInsights
                      @archivedInsights={{this.smartInsights.archivedInsightDays}}
                      @isLoading={{this.smartInsights.getArchivedInsightsTask.isRunning}}
                    />
                  </Content.tab>
                </TabsContainer.content>
              </PrismTabsContainer>
            {{else}}
              {{#if (eq @smartInsightsView SMART_INSIGHTS_VIEWS.LIST)}}
                <GeneralSmartInsightsListListSmartInsights
                  @insightsSorted={{this.sortedFilteredInsights}}
                  @selectSmartInsightsDay={{this.selectSmartInsightsDay}}
                />
              {{else}}
                <GeneralSmartInsightsCalendarCalendarSmartInsights
                  @insights={{this.filteredInsights}}
                  @selectSmartInsightsDay={{this.selectDate}}
                />
              {{/if}}
            {{/if}}
          </div>
        </div>
      </:content>
    </PrismDrawer>
  </template>
}
